import React from 'react';
import { Spotlight, SpotlightActionData } from '@mantine/spotlight';
import { rem } from '@mantine/core';
import { Search } from 'tabler-icons-react';
import { ThemedSpinner } from '../ui';
import { groupSpotlightActions } from './utils/searchHelpers';
import { COLORS } from '../../src/constants/theme';
import '../../styles/ThemedDrawer.css';

interface SpotlightSearchProps {
  actions: SpotlightActionData[];
  onSearchChange: (query: string) => void;
  onClose?: () => void;
  onOpen?: () => void;
  isSearching?: boolean;
  // Results are capped; the empty panel (recent + styles + quick actions) is not
  limit?: number;
}

const SpotlightSearch: React.FC<SpotlightSearchProps> = ({
  actions,
  onSearchChange,
  onClose,
  onOpen,
  isSearching = false,
  limit = 12,
}) => {
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(event.currentTarget.value);
  };

  return (
    <Spotlight
      actions={actions}
      nothingFound="No results found..."
      highlightQuery
      onSpotlightClose={onClose}
      onSpotlightOpen={onOpen}
      // Pass-through that keeps group headings; see groupSpotlightActions.
      filter={(_query, spotlightActions) => groupSpotlightActions(spotlightActions)}
      searchProps={{
        leftSection: <Search style={{ width: rem(20), height: rem(20) }} />,
        placeholder: 'Search dance styles, cities, artists, events, venues...',
        rightSection: isSearching ? <ThemedSpinner size="xs" aria-label="Searching" /> : null,
        onChange: handleSearchChange,
      }}
      // Background is pinned in ThemedDrawer.css: the panel is a Paper, and
      // the theme's Paper default bg is applied after styles.content. See #1165.
      // Placeholder styling lives there too: the styles prop can't target
      // ::placeholder.
      classNames={{ content: 'themed-spotlight-content', search: 'themed-spotlight-search' }}
      styles={{
        root: {
          '--spotlight-bg': COLORS.pageBgAlt,
          '--spotlight-border': COLORS.orange,
        },
        content: {
          backgroundColor: COLORS.pageBgAlt,
          maxHeight: '100vh',
        },
        actionsGroup: {
          maxHeight: '80vh',
          overflowY: 'auto',
        },
        actionsList: {
          maxHeight: '80vh',
          overflowY: 'auto',
          paddingBottom: actions.length > 6 ? rem(108) : 0,
        },
        action: {
          // Selected/hover states handled via CSS variables
          '--spotlight-action-highlight-color': COLORS.orange,
        },
        actionLabel: {
          color: '#ffffff',
          fontWeight: 700,
        },
        actionDescription: {
          color: COLORS.orange,
        },
        search: {
          backgroundColor: '#75757510',
          borderColor: COLORS.orange,
          color: '#ffffff',
        },
      }}
      shortcut={['mod + K', '/']}
      limit={limit}
    />
  );
};

export default SpotlightSearch;
